import classnames from 'classnames';
import * as React from 'react';
import { BodyText, Small } from 'palette/Typography';
import { ToggleError, ToggleIcon } from './components';
import { TInputType, TOnToggleInputChange, TVariant } from './types';
import './styles/Toggle.scss';

interface ToggleProps {
    className?: string;
    id: string;
    label?: string;
    htmlLabel?: string;
    name?: string;
    secondaryLabel?: string;
    variant?: TVariant;
    onChange?: TOnToggleInputChange;
    onLabelClick?: () => void;
    errorMessage?: string;
    propertyName?: string;
    value?: any;
    isDisabled?: boolean;
    isChecked?: boolean;
    isError?: boolean;
    level? : '2' | '1' | undefined;
}

function Toggle({
    variant = 'checkbox',
    id,
    label,
    htmlLabel,
    propertyName,
    onLabelClick,
    name,
    secondaryLabel,
    errorMessage,
    className,
    onChange,
    value,
    isChecked = false,
    isError = false,
    isDisabled = false,
    level = '2',
}: ToggleProps) {
    const classes = classnames('palette-toggle', `palette-toggle--${variant}`, {
        'palette-toggle--disabled': isDisabled,
    });
    const inputType: TInputType = variant === 'radio' ? 'radio' : 'checkbox';

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange?.(event);
    };

    const renderLabel = () => {
        if (htmlLabel) {
            return (
                /* eslint-disable-next-line jsx-a11y/no-static-element-interactions */
                <span
                    dangerouslySetInnerHTML={{ __html: htmlLabel }} // eslint-disable-line react/no-danger
                    onClick={onLabelClick}
                    onKeyDown={onLabelClick}
                />
            );
        }

        return label;
    };

    return (
        <label
            className={classnames(classes, className)}
            htmlFor={id}
        >
            <div className="palette-toggle__label-group">
                <input
                    checked={isChecked}
                    className={classnames('palette-toggle__input', {
                        'palette-toggle__input--disabled': isDisabled,
                    })}
                    disabled={isDisabled}
                    id={id}
                    name={name}
                    readOnly={onChange === undefined}
                    type={inputType}
                    value={value}
                    onChange={handleChange}
                    {...propertyName && { 'data-property': propertyName }}
                />
                <ToggleIcon
                    hasError={isError || !!errorMessage}
                    isChecked={isChecked}
                    isDisabled={isDisabled}
                    variant={variant}
                />
                {(label || htmlLabel) && (
                    <BodyText
                        className={classnames('palette-toggle__label', {
                            'palette-toggle__label--disabled': isDisabled,
                        })}
                        color={isDisabled ? 'lighter' : 'regular'}
                        level={level}
                    >
                        {renderLabel()}
                    </BodyText>
                )}

            </div>
            {secondaryLabel && (
                <Small color="light">
                    {secondaryLabel}
                </Small>
            )}
            {errorMessage && <ToggleError error={errorMessage!} />}
        </label>
    );
}

export default Toggle;
